/* Rococo — hero carousel (sections/rococo-hero-carousel.liquid) */
(() => {
    if (customElements.get('hero-carousel')) return;

    const mobileMq = window.matchMedia('(max-width: 767px)');
    const reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    class HeroCarousel extends HTMLElement {
        connectedCallback() {
            this.track = this.querySelector('[data-track]');
            this.slides = [...this.querySelectorAll('[data-slide]')];
            this.dots = [...this.querySelectorAll('[data-dot]')];
            this.pauseBtn = this.querySelector('[data-pause]');
            this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
            this.duration = Number(this.dataset.duration) || 6000;

            this.index = 0;
            this.elapsed = 0;
            this.holds = new Set();
            this.userPaused = reducedMq.matches;
            this.pendingAdvance = false;
            this.inView = true;

            this.onTick = this.onTick.bind(this);
            this.bindEvents();
            this.updatePausedUi();
            this.activate(0, { initial: true });
            this.raf = requestAnimationFrame(this.onTick);
        }

        disconnectedCallback() {
            cancelAnimationFrame(this.raf);
            this.io?.disconnect();
            document.removeEventListener('visibilitychange', this.onVisibility);
            mobileMq.removeEventListener?.('change', this.onMq);
        }

        /* ---------- state ---------- */

        get rotating() {
            return this.autoplay && !this.userPaused && this.holds.size === 0;
        }

        get videoMayPlay() {
            return !this.userPaused && this.inView && !document.hidden;
        }

        hold(reason) {
            this.holds.add(reason);
            this.syncVideo();
        }

        release(reason) {
            this.holds.delete(reason);
            if (this.pendingAdvance && this.rotating) {
                this.pendingAdvance = false;
                this.next();
                return;
            }
            this.syncVideo();
        }

        /* ---------- navigation ---------- */

        next() { this.go(this.index + 1); }
        prev() { this.go(this.index - 1); }

        go(i) {
            const n = this.slides.length;
            this.activate(((i % n) + n) % n);
        }

        activate(i, { initial = false } = {}) {
            const prev = this.slides[this.index];
            if (!initial && prev && i !== this.index) {
                const v = this.videoOf(prev);
                if (v) v.pause();
            }

            this.index = i;
            this.elapsed = 0;
            this.pendingAdvance = false;

            this.slides.forEach((slide, n) => {
                const on = n === i;
                slide.classList.toggle('is-active', on);
                slide.toggleAttribute('inert', !on);
                if (on) slide.removeAttribute('aria-hidden');
                else slide.setAttribute('aria-hidden', 'true');
            });
            this.dots.forEach((dot, n) => {
                if (n === i) dot.setAttribute('aria-current', 'true');
                else dot.removeAttribute('aria-current');
                dot.style.removeProperty('--rhc-progress');
            });

            this.setProgress(0);
            this.prepareVideo(this.slides[i], true);
            this.prepareVideo(this.slides[(i + 1) % this.slides.length], false);
            this.syncVideo();
        }

        /* ---------- video ---------- */

        videoOf(slide) {
            return slide?.querySelector('video') || null;
        }

        isPlayThrough(slide) {
            return slide?.dataset.type === 'video' && slide.dataset.playThrough === 'true' && this.autoplay;
        }

        prepareVideo(slide, active) {
            const video = this.videoOf(slide);
            if (!video) return;
            const src = mobileMq.matches ? video.dataset.srcM : video.dataset.srcD;
            if (!src) return;
            if (video.dataset.current !== src) {
                video.dataset.current = src;
                video.classList.remove('is-ready');
                video.src = src;
            }
            video.loop = !this.isPlayThrough(slide);
            video.preload = active ? 'auto' : 'metadata';

            if (!video.dataset.bound) {
                video.dataset.bound = '1';
                video.addEventListener('playing', () => video.classList.add('is-ready'));
                video.addEventListener('ended', () => {
                    if (this.slides[this.index] !== slide) return;
                    if (this.rotating) this.next();
                    else if (this.autoplay && !this.userPaused) this.pendingAdvance = true;
                });
                video.addEventListener('timeupdate', () => {
                    if (this.slides[this.index] === slide && this.isPlayThrough(slide) && video.duration) {
                        this.setProgress(video.currentTime / video.duration);
                    }
                });
            }
        }

        syncVideo() {
            const slide = this.slides[this.index];
            const video = this.videoOf(slide);
            if (!video) return;
            if (this.videoMayPlay) {
                if (video.ended) video.currentTime = 0;
                const p = video.play();
                if (p && p.catch) p.catch(() => {});
            } else {
                video.pause();
            }
        }

        /* ---------- timer ---------- */

        onTick(now) {
            const dt = this.last ? Math.min(now - this.last, 100) : 0;
            this.last = now;
            const slide = this.slides[this.index];

            if (this.rotating && this.inView && !document.hidden && !this.isPlayThrough(slide)) {
                this.elapsed += dt;
                this.setProgress(this.elapsed / this.duration);
                if (this.elapsed >= this.duration) this.next();
            }
            this.raf = requestAnimationFrame(this.onTick);
        }

        setProgress(p) {
            const dot = this.dots[this.index];
            if (!dot) return;
            // When nothing advances on its own, show the active indicator full.
            const value = this.autoplay ? Math.max(0, Math.min(1, p)) : 1;
            dot.style.setProperty('--rhc-progress', value.toFixed(4));
        }

        /* ---------- UI ---------- */

        togglePaused(force) {
            this.userPaused = typeof force === 'boolean' ? force : !this.userPaused;
            this.updatePausedUi();
            if (!this.userPaused && this.pendingAdvance && this.rotating) {
                this.pendingAdvance = false;
                this.next();
                return;
            }
            this.syncVideo();
        }

        updatePausedUi() {
            this.classList.toggle('is-paused', this.userPaused);
            this.track.setAttribute('aria-live', this.rotating ? 'off' : 'polite');
            if (this.pauseBtn) {
                this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
                this.pauseBtn.setAttribute('aria-label', this.userPaused ? 'Play banner' : 'Pause banner');
            }
        }

        bindEvents() {
            this.pauseBtn?.addEventListener('click', () => this.togglePaused());

            this.dots.forEach((dot, n) => {
                dot.addEventListener('click', () => this.go(n));
                dot.addEventListener('keydown', (e) => {
                    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                    e.preventDefault();
                    this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1));
                    this.dots[this.index].focus();
                });
            });

            // Pause rotation while the pointer or keyboard focus is in the banner.
            this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hold('hover'); });
            this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.release('hover'); });
            this.addEventListener('focusin', () => { this.hold('focus'); this.updatePausedUi(); });
            this.addEventListener('focusout', (e) => {
                if (!this.contains(e.relatedTarget)) { this.release('focus'); this.updatePausedUi(); }
            });

            // Swipe on touch / drag with mouse. A swipe never triggers the slide link.
            let startX = null, startY = 0, swiped = false;
            this.track.addEventListener('pointerdown', (e) => {
                if (this.slides.length < 2 || e.button !== 0) return;
                startX = e.clientX; startY = e.clientY; swiped = false;
            });
            this.track.addEventListener('pointerup', (e) => {
                if (startX === null) return;
                const dx = e.clientX - startX, dy = e.clientY - startY;
                startX = null;
                if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
                    swiped = true;
                    dx < 0 ? this.next() : this.prev();
                }
            });
            this.track.addEventListener('pointercancel', () => { startX = null; });
            this.track.addEventListener('click', (e) => {
                if (swiped) { e.preventDefault(); swiped = false; }
            }, true);

            // Stop when offscreen or the tab is hidden.
            if ('IntersectionObserver' in window) {
                this.io = new IntersectionObserver(([entry]) => {
                    this.inView = entry.isIntersecting;
                    this.syncVideo();
                }, { threshold: 0.2 });
                this.io.observe(this);
            }
            this.onVisibility = () => this.syncVideo();
            document.addEventListener('visibilitychange', this.onVisibility);

            // Swap to the mobile / desktop video when the breakpoint changes.
            this.onMq = () => {
                this.prepareVideo(this.slides[this.index], true);
                this.syncVideo();
            };
            mobileMq.addEventListener?.('change', this.onMq);

            // Theme editor: show the selected slide and hold it there.
            if (window.Shopify && window.Shopify.designMode) {
                document.addEventListener('shopify:block:select', (e) => {
                    const n = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
                    if (n < 0) return;
                    this.hold('editor');
                    this.go(n);
                });
                document.addEventListener('shopify:block:deselect', (e) => {
                    if (this.contains(e.target)) this.release('editor');
                });
            }
        }
    }

    customElements.define('hero-carousel', HeroCarousel);
})();
